import { LoadScript } from "@react-google-maps/api";
import React, { useEffect, useState } from "react";
import { AiOutlinePlus, AiOutlineDelete } from "react-icons/ai";
import { GoogleKeys } from "../../../../config/google";



const categories = {
  "Educational Institutes": ["school", "university"],
  "Hospitals": ["hospital"],
  "Shopping Centers": ["shopping_mall"],
  "Restaurants": ["restaurant"],
  "Parks": ["park"],
  "Public Transport": ["bus_station", "subway_station", "train_station"],
  "General Places": ["establishment"]
};


function LandmarksNearby({ formData, setFormData, handleLandmarkChange, addLandmark, removeLandmark, userLocation }) {
  const [autocompleteData, setAutocompleteData] = useState({});
  const [activeInput, setActiveInput] = useState(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);






  const fetchAutocomplete = (input, catIndex, placeIndex, category) => {
    if (!input) {
      setAutocompleteData((prev) => ({ ...prev, [`${catIndex}-${placeIndex}`]: [] }));
      return;
    }

    try {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.error("Google Maps API is not loaded");
        return;
      }

      const service = new window.google.maps.places.AutocompleteService();
      const placeTypes = categories[category] || categories["General Places"];
      let allPredictions = [];

      placeTypes.forEach((type) => {
        service.getPlacePredictions(
          {
            input,
            types: [type],
            componentRestrictions: { country: "IN" }
          },
          (predictions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              allPredictions = [...allPredictions, ...predictions];
              setAutocompleteData((prev) => ({
                ...prev,
                [`${catIndex}-${placeIndex}`]: allPredictions
              }));
            } else {
              console.warn("No predictions found or request failed", status);
            }
          }
        );
      });
    } catch (error) {
      console.error("Error fetching autocomplete suggestions:", error);
    }
  };

  const handlePlaceSelect = async (placeId, catIndex, placeIndex) => {
    try {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.error("Google Maps API is not loaded");
        return;
      }

      const service = new window.google.maps.places.PlacesService(document.createElement("div"));

      service.getDetails({ placeId }, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const name = place.formatted_address || place.name;
          const location = place.geometry?.location;
          let distance = "";
          let lat = "";
          let lng = "";

          if (location) {
            lat = location.lat();
            lng = location.lng();

            // Getting location from formData
            const projectlocation = formData.basicDetails.location;
            const userLat = projectlocation?.latitude;
            const userLng = projectlocation?.longitude;

            if (userLat && userLng && window.google?.maps?.geometry?.spherical) {
              const userLatLng = new window.google.maps.LatLng(userLat, userLng);
              const placeLatLng = new window.google.maps.LatLng(lat, lng);
              
              distance = (
                window.google.maps.geometry.spherical.computeDistanceBetween(userLatLng, placeLatLng) / 1000
              ).toFixed(2);
            } else {
              console.error("Google Maps Geometry library is not loaded or user location is missing.");
              distance = "N/A"; // Default value if distance can't be computed
            }
            
          }

          handleLandmarkChange({ target: { value: name } }, catIndex, "name", placeIndex);
          handleLandmarkChange({ target: { value: distance } }, catIndex, "distance", placeIndex);
          handleLandmarkChange({ target: { value: lat } }, catIndex, "latitude", placeIndex);
          handleLandmarkChange({ target: { value: lng } }, catIndex, "longitude", placeIndex);
          setAutocompleteData((prev) => ({ ...prev, [`${catIndex}-${placeIndex}`]: [] }));
        } else {
          console.warn("Failed to fetch place details", status);
        }
      });
    } catch (error) {
      console.error("Error selecting place:", error);
    }
  };

  const computeDistance = (userLatLng, placeLatLng, retries = 5) => {
    if (window.google?.maps?.geometry?.spherical) {
      return (
        (window.google.maps.geometry.spherical.computeDistanceBetween(userLatLng, placeLatLng) / 1000).toFixed(2)
      );
    } else if (retries > 0) {
      console.warn("Google Maps Geometry library is not loaded yet. Retrying...");
      setTimeout(() => computeDistance(userLatLng, placeLatLng, retries - 1), 1000); // Retry after 1 second
    } else {
      console.error("Google Maps Geometry library failed to load.");
      return "N/A";
    }
  };
  


  const fetchLandmarksByCategory = (catIndex, category) => {
    try {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.error("Google Maps API is not loaded");
        return;
      }

      const service = new window.google.maps.places.PlacesService(document.createElement("div"));
      const placeTypes = categories[category] || categories["General Places"];

      // Getting location from formData
      const location = formData.basicDetails.location;
      const userLat = location?.latitude;
      const userLng = location?.longitude;

      if (userLat && userLng) {
        const userLatLng = new window.google.maps.LatLng(userLat, userLng);
        const updatedLandmarks = [...formData.landmarks];

        let allResults = []; // Store all places from different types

        // Loop through each place type and fetch places
        let requestsCompleted = 0; // Track completed requests

        placeTypes.forEach((type) => {
          const request = {
            location: userLatLng,
            radius: 5000, // 5km radius
            type: type, // Querying for a specific type
          };

          service.nearbySearch(request, (results, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && results.length > 0) {
              // Process and store results
              const places = results.map((place) => {
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();

                // Calculate distance from project location
                const placeLatLng = new window.google.maps.LatLng(lat, lng);

                // const distance = (window.google.maps.geometry.spherical.computeDistanceBetween(userLatLng, placeLatLng) / 1000).toFixed(2) + " Km";
                const distance = computeDistance(userLatLng, placeLatLng);

                return {
                  name: place.name,
                  distance: distance,
                  latitude: lat,
                  longitude: lng,
                };
              });

              allResults = [...allResults, ...places]; // Merge places from different place types
            }

            // Check if all requests are completed
            requestsCompleted++;
            if (requestsCompleted === placeTypes.length) {
              updatedLandmarks[catIndex].places = allResults.slice(0, 10); // Set merged results


              console.log("Updated Landmarks:", updatedLandmarks);
              // Update formData with the new landmarks
              setFormData({
                ...formData,
                landmarks: updatedLandmarks,
              });

            }
          });
        });
      } else {
        console.warn("User location not found in formData");
      }
    } catch (error) {
      console.error("Error fetching places by category:", error);
    }
  };








  return (
    <fieldset className="border border-gray-300 p-4 rounded-md relative">
      <legend className="text-lg font-semibold px-2">Landmarks Nearby</legend>

      {formData.landmarks.map((category, catIndex) => (
        <div key={catIndex} className="mb-6 p-3 border border-gray-200 rounded-lg relative">
          <label className="block text-gray-700 font-medium mb-2">Category</label>
          <select
            value={category.category}
            onChange={(e) => {
              handleLandmarkChange(e, catIndex, "category");
              fetchLandmarksByCategory(catIndex, e.target.value);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            required
          >
            <option value="">Select Category</option>
            {Object.keys(categories).map((cat, index) => (
              <option key={index} value={cat}>{cat}</option>
            ))}
          </select>

          {category.places.map((place, placeIndex) => (
            <div key={placeIndex} className="relative mt-3">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={place.name}
                  onChange={(e) => {
                    handleLandmarkChange(e, catIndex, "name", placeIndex);
                    fetchAutocomplete(e.target.value, catIndex, placeIndex, category.category);
                  }}
                  onFocus={() => setActiveInput(`${catIndex}-${placeIndex}`)}
                  onBlur={() => setTimeout(() => setActiveInput(null), 200)}
                  placeholder="Landmark Name"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                  required
                />

                <input
                  type="number"
                  value={place.distance}
                  onChange={(e) => handleLandmarkChange(e, catIndex, "distance", placeIndex)}
                  placeholder="Distance (e.g. 1.7 Km)"
                  className="w-24 px-3 py-2 border border-gray-300 rounded-md"
                  required
                />

                <button
                  type="button"
                  onClick={() => removeLandmark(catIndex, placeIndex)}
                  className="p-2 text-red-500"
                >
                  <AiOutlineDelete size={20} />
                </button>
              </div>

              {autocompleteData[`${catIndex}-${placeIndex}`]?.length > 0 && activeInput === `${catIndex}-${placeIndex}` && (
                <ul className="absolute left-0 w-[60%] bg-white border border-gray-300 rounded-md mt-1 max-h-48 overflow-y-auto shadow-lg z-50">
                  {autocompleteData[`${catIndex}-${placeIndex}`].map((suggestion) => (
                    <li
                      key={suggestion.place_id}
                      onMouseDown={() => handlePlaceSelect(suggestion.place_id, catIndex, placeIndex)}
                      className="p-2 cursor-pointer hover:bg-gray-500 hover:text-white"
                    >
                      {suggestion.description}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={() => addLandmark(catIndex)}
            className="mt-3 px-3 py-2 text-blue-600 flex items-center gap-2"
          >
            <AiOutlinePlus /> Add Landmark
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={() => addLandmark()}
        className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-md flex items-center gap-2"
      >
        <AiOutlinePlus /> Add Category
      </button>
    </fieldset>

  );
}

export default LandmarksNearby;
